import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, RotateCcw, ShieldCheck, Trash2 } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

export interface QuotaOverrideItem {
  target: string;
  planTier: string;
  customCredits?: number | null;
  note: string;
  updatedAtEpochSec: number;
}

const QUOTA_API_BASE = 'https://modak.ugot.uk/api/v1/quota/admin';

export function UgotQuotaOverridesSection() {
  const [overrides, setOverrides] = useState<QuotaOverrideItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const [target, setTarget] = useState('');
  const [planTier, setPlanTier] = useState<'INTERNAL' | 'PRO' | 'LITE'>('INTERNAL');
  const [note, setNote] = useState('내부자 무과금 테스트 계정');

  const loadOverrides = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<{ ok: boolean; overrides?: QuotaOverrideItem[] }>(
        `${QUOTA_API_BASE}/overrides`,
      );
      setOverrides(res.data.overrides || []);
    } catch {
      // ignore transient error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOverrides();
  }, [loadOverrides]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = target.trim();
    if (!clean) return;
    setBusy('add');
    setMessage(null);
    try {
      const res = await axios.post<{ ok: boolean; message?: string }>(`${QUOTA_API_BASE}/overrides`, {
        target: clean,
        planTier,
        note: note.trim() || '내부자 무과금 테스트 계정',
      });
      setMessage({ tone: 'ok', text: res.data.message || `${clean} 계정을 내부자 프리패스로 등록했습니다.` });
      setTarget('');
      await loadOverrides();
    } catch (err: unknown) {
      const errMsg = axios.isAxiosError(err) ? err.response?.data?.message : null;
      setMessage({ tone: 'error', text: errMsg || '내부자 프리패스 설정 추가에 실패했습니다.' });
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (itemTarget: string) => {
    setBusy(`del:${itemTarget}`);
    setMessage(null);
    try {
      const res = await axios.delete<{ ok: boolean; message?: string }>(`${QUOTA_API_BASE}/overrides`, {
        params: { target: itemTarget },
      });
      setMessage({ tone: 'ok', text: res.data.message || `${itemTarget} 설정을 해제했습니다.` });
      await loadOverrides();
    } catch {
      setMessage({ tone: 'error', text: '설정 해제에 실패했습니다.' });
    } finally {
      setBusy(null);
    }
  };

  const handleResetUsage = async (itemTarget = '*') => {
    setBusy(`reset:${itemTarget}`);
    setMessage(null);
    try {
      const res = await axios.post<{ ok: boolean; message?: string }>(
        `${QUOTA_API_BASE}/reset-usage`,
        null,
        { params: { target: itemTarget } },
      );
      setMessage({ tone: 'ok', text: res.data.message || '이번 달 크레딧 사용량을 초기화했습니다.' });
    } catch {
      setMessage({ tone: 'error', text: '사용량 초기화에 실패했습니다.' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card data-testid="ugot-quota-overrides-card">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            통합 크레딧(Live STT · Modak 3D · Browser Agent) 내부자 무과금 테스트 설정
            <Badge variant="success">{overrides.length}개 활성</Badge>
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            여기에 등록된 이메일·사용자 ID·확장프로그램 테스트 키는 결제 없이 통합 크레딧(INTERNAL 999,999 cr/월)으로 모든 AI 기능을 무료 테스트할 수 있습니다.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy !== null}
          onClick={() => void handleResetUsage('*')}
        >
          <RotateCcw className="mr-1 h-3.5 w-3.5" />
          이번 달 사용량 전체 0 cr 초기화
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {message && (
          <div
            className={
              message.tone === 'error'
                ? 'rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800'
                : 'rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900'
            }
          >
            {message.text}
          </div>
        )}

        <form
          className="grid gap-2 sm:grid-cols-[minmax(0,1.4fr)_180px_minmax(0,1fr)_auto]"
          onSubmit={handleAdd}
        >
          <Input
            placeholder="이메일, User ID 또는 확장 테스트 키 (예: dudnjsckrgo@gmail.com)"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="quota-override-target"
          />
          <select
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
            value={planTier}
            onChange={(e) => setPlanTier(e.target.value as 'INTERNAL' | 'PRO' | 'LITE')}
          >
            <option value="INTERNAL">INTERNAL (999,999 cr · 무과금)</option>
            <option value="PRO">PRO (7,200 cr 테스트)</option>
            <option value="LITE">LITE (3,000 cr 테스트)</option>
          </select>
          <Input
            placeholder="메모 (예: 내부자 테스트)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button type="submit" disabled={busy !== null || !target.trim()}>
            {busy === 'add' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            추가
          </Button>
        </form>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>대상 (이메일 / User ID / 키)</TableHead>
              <TableHead>부여 등급 (월 한도)</TableHead>
              <TableHead>메모</TableHead>
              <TableHead className="text-right">관리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {overrides.map((item) => (
              <TableRow key={item.target}>
                <TableCell className="font-mono text-xs">{item.target}</TableCell>
                <TableCell>
                  <Badge variant={item.planTier === 'INTERNAL' ? 'success' : 'secondary'}>
                    {item.planTier} ({(item.customCredits ?? 999999).toLocaleString()} cr)
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{item.note || '-'}</TableCell>
                <TableCell className="space-x-1 text-right">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busy !== null}
                    onClick={() => void handleResetUsage(item.target)}
                  >
                    사용량 초기화
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    disabled={busy !== null}
                    onClick={() => void handleRemove(item.target)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {overrides.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={4} className="py-4 text-center text-xs text-muted-foreground">
                  등록된 내부자 프리패스 계정이 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
